function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.applyFallbackPriceFilter();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.applyFallbackPriceFilter();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const countElement = parsedHTML.getElementById('ProductCount') || parsedHTML.getElementById('ProductCountDesktop');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (!countElement) return;

    if (container) {
      container.innerHTML = countElement.innerHTML;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = countElement.innerHTML;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    FacetFiltersForm.applyFallbackPriceFilter(searchParams);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  static applyFallbackPriceFilter(searchParams = window.location.search.slice(1)) {
    const params = new URLSearchParams(searchParams);
    const minValue = Number.parseFloat(params.get('filter.v.price.gte'));
    const maxValue = Number.parseFloat(params.get('filter.v.price.lte'));
    const hasMin = !Number.isNaN(minValue) && minValue > 0;
    const hasMax = !Number.isNaN(maxValue) && maxValue > 0;

    if (!hasMin && !hasMax) {
      document.querySelectorAll('[data-price-hidden="true"]').forEach((element) => {
        element.style.display = element.dataset.priceOriginalDisplay || '';
        element.removeAttribute('data-price-original-display');
        element.removeAttribute('data-price-hidden');
      });
      document.querySelectorAll('[data-price-no-results="true"]').forEach((element) => element.remove());
      FacetFiltersForm.restoreFallbackPriceCount();
      return;
    }

    const minCents = hasMin ? Math.round(minValue * 100) : 0;
    const maxCents = hasMax ? Math.round(maxValue * 100) : Number.POSITIVE_INFINITY;
    const productCards = document.querySelectorAll('#ProductGridContainer .grid-view-item[data-product-price]');
    let visibleCount = 0;

    productCards.forEach((card) => {
      const gridItem = card.closest('.grid__item') || card;
      const price = Number.parseInt(card.dataset.productPrice, 10);
      const isVisible = price >= minCents && price <= maxCents;

      if (isVisible) {
        gridItem.style.display = gridItem.dataset.priceOriginalDisplay || '';
        gridItem.removeAttribute('data-price-original-display');
        gridItem.removeAttribute('data-price-hidden');
        visibleCount += 1;
      } else {
        if (!gridItem.dataset.priceOriginalDisplay) {
          gridItem.dataset.priceOriginalDisplay = gridItem.style.display || '';
        }
        gridItem.style.display = 'none';
        gridItem.setAttribute('data-price-hidden', 'true');
      }
    });

    document.querySelectorAll('[data-price-no-results="true"]').forEach((element) => element.remove());

    if (productCards.length > 0 && visibleCount === 0) {
      const grid = document.querySelector('#ProductGridContainer .collection-main-body-inner');
      if (grid) {
        const emptyMessage = document.createElement('div');
        emptyMessage.className = 'grid__item';
        emptyMessage.setAttribute('data-price-no-results', 'true');
        emptyMessage.innerHTML = '<p>No products match this price range.</p>';
        grid.appendChild(emptyMessage);
      }
    }

    FacetFiltersForm.updateFallbackPriceCount(visibleCount);
  }

  static updateFallbackPriceCount(visibleCount) {
    document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach((element) => {
      if (!element.dataset.priceOriginalCount) {
        element.dataset.priceOriginalCount = element.textContent;
      }

      element.textContent = element.dataset.priceOriginalCount.replace(/\d+/, visibleCount);
    });
  }

  static restoreFallbackPriceCount() {
    document.querySelectorAll('[data-price-original-count]').forEach((element) => {
      element.textContent = element.dataset.priceOriginalCount;
      delete element.dataset.priceOriginalCount;
    });
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.applyFallbackPriceFilter());
} else {
  FacetFiltersForm.applyFallbackPriceFilter();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.inputs = Array.from(this.querySelectorAll('input'));
    this.minInput = this.inputs[0];
    this.maxInput = this.inputs[1];
    this.slider = this.querySelector('.price-range-slider');
    this.track = this.querySelector('.price-range-slider__track');
    this.range = this.querySelector('.price-range-slider__range');
    this.minThumb = this.querySelector('.price-range-slider__thumb--min');
    this.maxThumb = this.querySelector('.price-range-slider__thumb--max');
    this.min = 0;
    this.max = this.parsePrice(this.slider?.dataset.max || this.maxInput?.dataset.max || 0);
    this.restoreValuesFromURL();

    this.inputs.forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('input', this.onRangeInput.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
    this.setupSlider();
    this.updateSlider();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.updateSlider();
  }

  onRangeInput() {
    this.updateSlider();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const minInput = this.minInput;
    const maxInput = this.maxInput;
    if (!minInput || !maxInput) return;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = this.parsePrice(input.value);
    const min = this.parsePrice(input.getAttribute('data-min'));
    const max = this.parsePrice(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  restoreValuesFromURL() {
    if (!this.minInput || !this.maxInput) return;

    const params = new URLSearchParams(window.location.search);
    const minValue = params.get(this.minInput.name);
    const maxValue = params.get(this.maxInput.name);

    if (minValue !== null && this.minInput.value === '') this.minInput.value = minValue;
    if (maxValue !== null && this.maxInput.value === '') this.maxInput.value = maxValue;
  }

  setupSlider() {
    if (!this.track || !this.minThumb || !this.maxThumb || !this.minInput || !this.maxInput || this.max <= this.min) return;

    this.track.addEventListener('click', (event) => {
      if (event.target.classList.contains('price-range-slider__thumb')) return;

      const value = this.valueFromPointer(event.clientX);
      const minValue = this.currentMinValue();
      const maxValue = this.currentMaxValue();
      const targetInput = Math.abs(value - minValue) <= Math.abs(value - maxValue) ? this.minInput : this.maxInput;

      targetInput.value = this.formatPrice(value);
      this.adjustToValidValues(targetInput);
      this.setMinAndMaxValues();
      this.updateSlider();
      this.submitPriceRange();
    });

    this.addThumbEvents(this.minThumb, this.minInput);
    this.addThumbEvents(this.maxThumb, this.maxInput);
  }

  addThumbEvents(thumb, input) {
    thumb.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      thumb.setPointerCapture(event.pointerId);
      thumb.classList.add('is-dragging');

      const onPointerMove = (moveEvent) => {
        input.value = this.formatPrice(this.valueFromPointer(moveEvent.clientX));
        this.adjustToValidValues(input);
        this.setMinAndMaxValues();
        this.updateSlider();
      };

      const onPointerUp = () => {
        thumb.classList.remove('is-dragging');
        thumb.removeEventListener('pointermove', onPointerMove);
        thumb.removeEventListener('pointerup', onPointerUp);
        thumb.removeEventListener('pointercancel', onPointerUp);
        this.submitPriceRange();
      };

      thumb.addEventListener('pointermove', onPointerMove);
      thumb.addEventListener('pointerup', onPointerUp);
      thumb.addEventListener('pointercancel', onPointerUp);
    });
  }

  updateSlider() {
    if (!this.range || !this.minThumb || !this.maxThumb || this.max <= this.min) return;

    const minPercent = this.percentFromValue(this.currentMinValue());
    const maxPercent = this.percentFromValue(this.currentMaxValue());

    this.minThumb.style.left = `${minPercent}%`;
    this.maxThumb.style.left = `${maxPercent}%`;
    this.range.style.left = `${minPercent}%`;
    this.range.style.right = `${100 - maxPercent}%`;
  }

  currentMinValue() {
    return this.clamp(this.parsePrice(this.minInput.value || this.minInput.placeholder || this.min), this.min, this.currentMaxValue());
  }

  currentMaxValue() {
    return this.clamp(this.parsePrice(this.maxInput.value || this.maxInput.placeholder || this.max), this.currentMinValueRaw(), this.max);
  }

  currentMinValueRaw() {
    return this.clamp(this.parsePrice(this.minInput.value || this.minInput.placeholder || this.min), this.min, this.max);
  }

  valueFromPointer(clientX) {
    const rect = this.track.getBoundingClientRect();
    const percent = this.clamp((clientX - rect.left) / rect.width, 0, 1);

    return this.min + percent * (this.max - this.min);
  }

  percentFromValue(value) {
    return ((value - this.min) / (this.max - this.min)) * 100;
  }

  parsePrice(value) {
    if (value === null || value === undefined || value === '') return 0;

    const normalized = String(value).trim().replace(/[\s']/g, '');
    const decimalValue =
      normalized.includes(',') && !normalized.includes('.')
        ? normalized.replace(',', '.')
        : normalized.replace(/,/g, '');

    return Number.parseFloat(decimalValue) || 0;
  }

  formatPrice(value) {
    return String(Math.round(this.clamp(value, this.min, this.max)));
  }

  clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  submitPriceRange() {
    this.minInput.value = this.formatPrice(this.currentMinValue());
    this.maxInput.value = this.formatPrice(this.currentMaxValue());
    this.setMinAndMaxValues();
    this.updateSlider();
    this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
